import React, { useEffect, useState } from "react";
import {
  FaCheckCircle,
  FaCloud,
  FaCopy,
  FaExclamationTriangle,
  FaInfoCircle,
  FaPaperPlane,
  FaSyncAlt,
  FaTrash,
} from "react-icons/fa";
import api from "../utils/axiosInstance";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Input,
  InputGroup,
  PageHeader,
  Spinner,
  Table,
  TableWrapper,
  Td,
  Textarea,
  Th,
} from "../components/ui";

const emptyForm = {
  wabaId: "",
  phoneNumberId: "",
  accessToken: "",
  appSecret: "",
};

const defaultTemplateDraft = {
  name: "",
  language: "en_US",
  category: "UTILITY",
  body: "Hi {{1}}, thanks for your interest.",
  bodySamples: "Alex",
};

const setupSteps = [
  "Create or select a Meta app with WhatsApp enabled.",
  "Copy the WhatsApp Business Account ID and Phone Number ID from WhatsApp Manager.",
  "Generate a system-user access token with whatsapp_business_management and whatsapp_business_messaging.",
  "Connect the session here, then copy the Callback URL and Verify token into Meta Webhooks.",
  "Subscribe the webhook to messages and run Probe token after Meta verifies the callback.",
];

const webhookSteps = [
  "Meta Webhook object: WhatsApp Business Account.",
  "Callback URL: use the URL shown by Walytic for this session.",
  "Verify token: use the token shown by Walytic for this session.",
  "Webhook fields: subscribe to messages for inbound replies, status updates, and flow triggers.",
  "App Secret is recommended so Walytic can validate signed webhook payloads.",
];

const helpText = {
  wabaId: "The WhatsApp Business Account ID from Meta Business Manager. Walytic uses it for template sync and submissions.",
  phoneNumberId: "The Meta phone_number_id for the Cloud API number. This is not the public display phone number.",
  accessToken: "Use a long-lived system-user or embedded-signup token with WhatsApp management and messaging permissions.",
  appSecret: "Optional but recommended. Enables signed webhook payload verification with the Meta app secret.",
};

function HelpTip({ text }) {
  return (
    <span
      className="inline-flex items-center text-ink-muted"
      title={text}
      aria-label={text}
    >
      <FaInfoCircle size={12} />
    </span>
  );
}

function LabelWithTip({ children, tip }) {
  return (
    <span className="inline-flex items-center gap-1">
      {children}
      <HelpTip text={tip} />
    </span>
  );
}

export default function CloudSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [connected, setConnected] = useState(null);
  const [error, setError] = useState("");
  const [busyBySession, setBusyBySession] = useState({});
  const [templatesBySession, setTemplatesBySession] = useState({});
  const [templateDraftBySession, setTemplateDraftBySession] = useState({});
  const [templateStatusBySession, setTemplateStatusBySession] = useState({});

  async function fetchTemplates(sessionId) {
    setTemplateStatusBySession((current) => ({ ...current, [sessionId]: "loading" }));
    try {
      const res = await api.get(`/api/cloud/sessions/${sessionId}/templates`);
      setTemplatesBySession((current) => ({ ...current, [sessionId]: res?.data?.templates || [] }));
      setTemplateStatusBySession((current) => ({ ...current, [sessionId]: null }));
    } catch (err) {
      setTemplateStatusBySession((current) => ({
        ...current,
        [sessionId]: err?.response?.data?.message || err.message,
      }));
    }
  }

  const fetchSessions = async () => {
    try {
      const res = await api.get("/api/cloud/sessions");
      const rows = res?.data?.sessions || [];
      setSessions(rows);
      setError("");
      rows.forEach((session) => fetchTemplates(session.sessionId));
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const setBusy = (sessionId, value) => {
    setBusyBySession((current) => ({ ...current, [sessionId]: value }));
  };

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const updateTemplateDraft = (sessionId, key, value) => {
    setTemplateDraftBySession((current) => ({
      ...current,
      [sessionId]: { ...(current[sessionId] || defaultTemplateDraft), [key]: value },
    }));
  };

  const draftToPayload = (draft) => ({
    name: draft.name,
    language: draft.language || "en_US",
    category: draft.category || "UTILITY",
    components: [
      {
        type: "BODY",
        text: draft.body || "",
        example: {
          body_text: [
            String(draft.bodySamples || "")
              .split(",")
              .map((part) => part.trim())
              .filter(Boolean),
          ],
        },
      },
    ],
  });

  const saveTemplateDraft = async (sessionId) => {
    const draft = templateDraftBySession[sessionId] || defaultTemplateDraft;
    setBusy(sessionId, "template-save");
    setError("");
    try {
      await api.post(`/api/cloud/sessions/${sessionId}/templates`, draftToPayload(draft));
      setTemplateDraftBySession((current) => ({ ...current, [sessionId]: defaultTemplateDraft }));
      await fetchTemplates(sessionId);
    } catch (err) {
      setError(err?.response?.data?.errors?.[0]?.message || err?.response?.data?.message || err.message);
    } finally {
      setBusy(sessionId, null);
    }
  };

  const syncTemplates = async (sessionId) => {
    setBusy(sessionId, "template-sync");
    setError("");
    try {
      await api.post(`/api/cloud/sessions/${sessionId}/templates/sync`);
      await fetchTemplates(sessionId);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(sessionId, null);
    }
  };

  const submitTemplate = async (sessionId, templateId) => {
    setBusy(sessionId, `template-submit-${templateId}`);
    setError("");
    try {
      await api.post(`/api/cloud/sessions/${sessionId}/templates/${templateId}/submit`);
      await fetchTemplates(sessionId);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(sessionId, null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    setConnected(null);

    try {
      const res = await api.post("/api/cloud/sessions", form);
      if (!res?.data?.success) throw new Error(res?.data?.error || "Cloud session failed");
      setConnected(res.data);
      setForm(emptyForm);
      setShowForm(false);
      await fetchSessions();
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleProbe = async (sessionId) => {
    setBusy(sessionId, "probe");
    setError("");
    try {
      await api.post(`/api/cloud/sessions/${sessionId}/probe`);
      await fetchSessions();
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message);
    } finally {
      setBusy(sessionId, null);
    }
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm("Disconnect this Cloud session?")) return;
    setBusy(sessionId, "delete");
    setError("");
    try {
      await api.delete(`/api/cloud/sessions/${sessionId}`);
      await fetchSessions();
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message);
    } finally {
      setBusy(sessionId, null);
    }
  };

  const handleTestSend = async (sessionId) => {
    const to = window.prompt("Recipient phone number with country code");
    if (!to) return;
    const text = window.prompt("Message text", "Hello from Walytic Cloud");
    if (!text) return;

    setBusy(sessionId, "send");
    setError("");
    try {
      const res = await api.post(`/api/cloud/sessions/${sessionId}/test-send`, { to, text });
      window.alert(`Sent: ${res?.data?.messageId || "accepted"}`);
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message);
    } finally {
      setBusy(sessionId, null);
    }
  };

  const copy = async (value) => {
    if (!value) return;
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value).catch(() => {});
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        title="WhatsApp Cloud API"
        subtitle="Manage Meta-hosted WhatsApp Business sessions."
      >
        {!showForm && (
          <Button onClick={() => { setShowForm(true); setConnected(null); setError(""); }}>
            <FaCloud size={12} className="mr-2" />
            Add Cloud Session
          </Button>
        )}
      </PageHeader>

      <Card className="mb-6">
        <CardHeader className="flex items-center justify-between gap-3">
          <span>Cloud API setup guide</span>
          <Badge variant="brand">Workspace scoped</Badge>
        </CardHeader>
        <CardBody>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-lg border border-surface-border bg-surface-card p-4">
              <p className="text-sm font-semibold text-ink-primary">Before connecting</p>
              <ol className="mt-3 space-y-2 text-sm text-ink-muted list-decimal list-inside">
                {setupSteps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </div>
            <div className="rounded-lg border border-surface-border bg-surface-card p-4">
              <p className="text-sm font-semibold text-ink-primary">Meta webhook settings</p>
              <p className="mt-1 text-xs text-ink-muted">
                The exact Callback URL and Verify token are generated after a Cloud session connects, then stay visible under that session.
              </p>
              <ol className="mt-3 space-y-2 text-sm text-ink-muted list-decimal list-inside">
                {webhookSteps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </div>
          </div>
        </CardBody>
      </Card>

      {error && (
        <Alert variant="error" className="mb-4">
          <FaExclamationTriangle className="inline mr-2" />
          {error}
        </Alert>
      )}

      {connected && (
        <Card className="mb-6">
          <CardHeader className="flex items-center gap-2">
            <FaCheckCircle className="text-green-600" />
            <span>Cloud session connected</span>
          </CardHeader>
          <CardBody>
            <div className="grid md:grid-cols-2 gap-4">
              <InputGroup label="Callback URL">
                <div className="flex gap-2">
                  <Input readOnly value={connected.webhook?.url || ""} className="font-mono text-xs" />
                  <Button type="button" variant="ghost" onClick={() => copy(connected.webhook?.url)} title="Copy URL">
                    <FaCopy />
                  </Button>
                </div>
              </InputGroup>
              <InputGroup label="Verify token">
                <div className="flex gap-2">
                  <Input readOnly value={connected.webhook?.verifyToken || ""} className="font-mono text-xs" />
                  <Button type="button" variant="ghost" onClick={() => copy(connected.webhook?.verifyToken)} title="Copy token">
                    <FaCopy />
                  </Button>
                </div>
              </InputGroup>
            </div>
            <p className="input-hint mt-2">
              In Meta, paste these under WhatsApp Business Account webhooks and subscribe to messages.
            </p>
            <div className="mt-3 flex items-center gap-2 text-sm text-ink-muted">
              <span>Display phone: +{connected.displayPhone || "unknown"}</span>
              {connected.qualityRating && (
                <Badge variant={connected.qualityRating === "GREEN" ? "success" : "warning"}>
                  {connected.qualityRating}
                </Badge>
              )}
              <span>Signature: {connected.signatureVerification}</span>
            </div>
          </CardBody>
        </Card>
      )}

      {showForm && (
        <Card className="mb-6">
          <CardHeader>Connect Cloud session</CardHeader>
          <CardBody>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <InputGroup label={<LabelWithTip tip={helpText.wabaId}>WhatsApp Business Account ID</LabelWithTip>}>
                  <Input
                    value={form.wabaId}
                    onChange={(e) => updateForm("wabaId", e.target.value.trim())}
                    placeholder="288360351017362"
                    required
                  />
                </InputGroup>
                <InputGroup label={<LabelWithTip tip={helpText.phoneNumberId}>Phone Number ID</LabelWithTip>}>
                  <Input
                    value={form.phoneNumberId}
                    onChange={(e) => updateForm("phoneNumberId", e.target.value.trim())}
                    placeholder="275658882294251"
                    required
                  />
                </InputGroup>
              </div>

              <InputGroup
                label={<LabelWithTip tip={helpText.accessToken}>Access Token</LabelWithTip>}
                hint="Stored encrypted at rest. Needs WhatsApp Business management and messaging permissions."
              >
                <Textarea
                  value={form.accessToken}
                  onChange={(e) => updateForm("accessToken", e.target.value.trim())}
                  placeholder="EAA..."
                  rows={3}
                  required
                />
              </InputGroup>

              <InputGroup
                label={<LabelWithTip tip={helpText.appSecret}>App Secret</LabelWithTip>}
                hint="Recommended for signed webhook verification."
              >
                <Input
                  value={form.appSecret}
                  onChange={(e) => updateForm("appSecret", e.target.value.trim())}
                  placeholder="Meta app secret"
                  type="password"
                />
              </InputGroup>

              <div className="flex gap-2">
                <Button type="submit" disabled={submitting}>
                  {submitting ? <Spinner size="sm" /> : "Connect"}
                </Button>
                <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader>Connected Cloud sessions</CardHeader>
        <CardBody className="p-0">
          {loading ? (
            <div className="p-6"><Spinner /></div>
          ) : sessions.length === 0 ? (
            <EmptyState
              icon={FaCloud}
              title="No Cloud sessions"
              description="Add a Cloud session to generate the Meta Callback URL and Verify token."
            />
          ) : (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Number</Th>
                    <Th>WABA / Phone ID</Th>
                    <Th>Status</Th>
                    <Th>Last activity</Th>
                    <Th className="text-right">Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session) => {
                    const draft = templateDraftBySession[session.sessionId] || defaultTemplateDraft;
                    const templates = templatesBySession[session.sessionId] || [];
                    const templateStatus = templateStatusBySession[session.sessionId];
                    const webhookUrl = session.webhook?.url || session.webhookUrl || "";
                    const webhookVerifyToken = session.webhook?.verifyToken || session.webhookVerifyToken || "";
                    return (
                      <React.Fragment key={session.sessionId}>
                        <tr>
                          <Td className="font-mono text-xs">+{session.number || "-"}</Td>
                          <Td>
                            <div className="font-mono text-xs text-ink-muted">
                              <div>WABA: {session.wabaId || "-"}</div>
                              <div>Phone: {session.phoneNumberId}</div>
                            </div>
                          </Td>
                          <Td>
                            <Badge variant={session.status === "connected" ? "success" : "warning"}>
                              {session.status}
                            </Badge>
                          </Td>
                          <Td className="text-xs text-ink-muted">
                            {session.lastMessageTime ? new Date(session.lastMessageTime).toLocaleString() : "-"}
                          </Td>
                          <Td className="text-right whitespace-nowrap">
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => handleProbe(session.sessionId)}
                              disabled={!!busyBySession[session.sessionId]}
                              title="Probe token"
                            >
                              {busyBySession[session.sessionId] === "probe" ? <Spinner size="sm" /> : <FaSyncAlt />}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => handleTestSend(session.sessionId)}
                              disabled={!!busyBySession[session.sessionId]}
                              title="Test send"
                            >
                              {busyBySession[session.sessionId] === "send" ? <Spinner size="sm" /> : <FaPaperPlane />}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => handleDelete(session.sessionId)}
                              disabled={!!busyBySession[session.sessionId]}
                              title="Disconnect"
                            >
                              {busyBySession[session.sessionId] === "delete" ? <Spinner size="sm" /> : <FaTrash className="text-red-500" />}
                            </Button>
                          </Td>
                        </tr>
                        <tr>
                          <Td colSpan={5} className="bg-surface-subtle/40">
                            <div className="mb-4 rounded-lg border border-surface-border bg-surface-card p-3">
                              <div className="flex items-start justify-between gap-3 mb-3">
                                <div>
                                  <p className="text-xs font-semibold text-ink-primary">Webhook</p>
                                  <p className="text-2xs text-ink-muted">
                                    Use these values in Meta App Dashboard > WhatsApp > Configuration.
                                  </p>
                                </div>
                                <Badge variant="brand">messages</Badge>
                              </div>
                              <div className="grid gap-3 lg:grid-cols-2">
                                <InputGroup label="Callback URL">
                                  <div className="flex gap-2">
                                    <Input readOnly value={webhookUrl} className="font-mono text-xs" />
                                    <Button type="button" variant="ghost" onClick={() => copy(webhookUrl)} title="Copy callback URL">
                                      <FaCopy />
                                    </Button>
                                  </div>
                                </InputGroup>
                                <InputGroup label="Verify token">
                                  <div className="flex gap-2">
                                    <Input readOnly value={webhookVerifyToken} className="font-mono text-xs" />
                                    <Button type="button" variant="ghost" onClick={() => copy(webhookVerifyToken)} title="Copy verify token">
                                      <FaCopy />
                                    </Button>
                                  </div>
                                </InputGroup>
                              </div>
                              <p className="input-hint mt-2">
                                After Meta verifies the callback, keep the messages field subscribed so inbound replies reach inbox, flows, and campaigns.
                              </p>
                            </div>
                            <div className="grid gap-4 lg:grid-cols-[1fr_1.1fr]">
                              <div>
                                <div className="flex items-center justify-between gap-2 mb-2">
                                  <p className="text-xs font-semibold text-ink-primary">Templates</p>
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => syncTemplates(session.sessionId)}
                                    disabled={!!busyBySession[session.sessionId]}
                                  >
                                    {busyBySession[session.sessionId] === "template-sync" ? <Spinner size="sm" /> : <FaSyncAlt size={10} className="mr-1" />}
                                    Sync
                                  </Button>
                                </div>
                                {templateStatus === "loading" ? (
                                  <div className="py-3"><Spinner size="sm" /></div>
                                ) : templates.length === 0 ? (
                                  <p className="text-xs text-ink-muted">No templates synced yet.</p>
                                ) : (
                                  <div className="divide-y divide-surface-border rounded-lg border border-surface-border bg-surface-card">
                                    {templates.map((tpl) => (
                                      <div key={tpl.id} className="flex items-center justify-between gap-3 p-2">
                                        <div className="min-w-0">
                                          <p className="text-xs font-medium text-ink-primary truncate">{tpl.name}</p>
                                          <p className="text-2xs text-ink-muted">{tpl.language} · {tpl.category}</p>
                                          {tpl.rejectionReason && <p className="text-2xs text-red-600 mt-1">{tpl.rejectionReason}</p>}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Badge variant={tpl.status === "APPROVED" ? "success" : tpl.status === "REJECTED" ? "danger" : "warning"}>
                                            {tpl.status}
                                          </Badge>
                                          {tpl.status === "DRAFT" && (
                                            <Button
                                              type="button"
                                              variant="secondary"
                                              size="sm"
                                              onClick={() => submitTemplate(session.sessionId, tpl.id)}
                                              disabled={!!busyBySession[session.sessionId]}
                                            >
                                              {busyBySession[session.sessionId] === `template-submit-${tpl.id}` ? <Spinner size="sm" /> : "Submit"}
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {templateStatus && templateStatus !== "loading" && (
                                  <p className="text-2xs text-red-600 mt-2">{templateStatus}</p>
                                )}
                              </div>
                              <div className="rounded-lg border border-surface-border bg-surface-card p-3">
                                <div className="grid sm:grid-cols-3 gap-2">
                                  <Input
                                    value={draft.name}
                                    onChange={(e) => updateTemplateDraft(session.sessionId, "name", e.target.value)}
                                    placeholder="template_name"
                                    className="text-xs"
                                  />
                                  <Input
                                    value={draft.language}
                                    onChange={(e) => updateTemplateDraft(session.sessionId, "language", e.target.value)}
                                    placeholder="en_US"
                                    className="text-xs"
                                  />
                                  <select
                                    className="input text-xs"
                                    value={draft.category}
                                    onChange={(e) => updateTemplateDraft(session.sessionId, "category", e.target.value)}
                                  >
                                    <option value="UTILITY">Utility</option>
                                    <option value="MARKETING">Marketing</option>
                                    <option value="AUTHENTICATION">Authentication</option>
                                  </select>
                                </div>
                                <Textarea
                                  value={draft.body}
                                  onChange={(e) => updateTemplateDraft(session.sessionId, "body", e.target.value)}
                                  rows={3}
                                  className="text-xs mt-2"
                                />
                                <Input
                                  value={draft.bodySamples}
                                  onChange={(e) => updateTemplateDraft(session.sessionId, "bodySamples", e.target.value)}
                                  placeholder="Sample values for {{1}}, {{2}}"
                                  className="text-xs mt-2"
                                />
                                <div className="flex justify-end mt-2">
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => saveTemplateDraft(session.sessionId)}
                                    disabled={!!busyBySession[session.sessionId] || !draft.name.trim() || !draft.body.trim()}
                                  >
                                    {busyBySession[session.sessionId] === "template-save" ? <Spinner size="sm" /> : "Save draft"}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </Td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrapper>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
